import React from 'react';
import { Collapse } from 'antd';
import { CategoryNameLabel } from '../categories/categoryNameLabel';
import { CategoryBranchCollapse, CategoryTreeLevel, PanelHeader } from './index.styled';

const { Panel } = Collapse;

export interface defaultCategoryType {
  attributes?: {
    parent?: {
      data?: {
        id?: string | null;
      } | null;
    } | null;
    subCategories?: {
      data?: Array<{ id?: string | null } | null> | null;
    } | null;
    title?: string | null;
    translatedName?: string | null;
    description?: string | null;
    order?: number | null;
  } | null;
  id?: string | null;
}
interface categoryCollapseTreeProps<Item> {
  renderItem: (category: Item) => React.ReactElement | null;
  categories?: Item[];
}

function categoryCollapseTree<T extends defaultCategoryType>({
  renderItem,
  categories = [],
}: categoryCollapseTreeProps<T>) {
  const parentCategories = (categories || [])
    .filter((category) => !category.attributes?.parent?.data)
    .sort((a, b) => ((a.attributes?.order ?? 0) > (b.attributes?.order ?? 0) ? 1 : -1));

  const findSubCategories = (parentId: string) => {
    const parent = categories.find((category) => String(category.id) === parentId);
    const linkedIds = new Set(
      (parent?.attributes?.subCategories?.data ?? [])
        .map((sub) => (sub?.id ? String(sub.id) : null))
        .filter((id): id is string => Boolean(id)),
    );

    return (categories || [])
      .filter(
        (category) =>
          category.attributes?.parent?.data?.id === parentId ||
          (category.id && linkedIds.has(String(category.id))),
      )
      .sort((a, b) => ((a.attributes?.order ?? 0) > (b.attributes?.order ?? 0) ? 1 : -1));
  };

  const hasSubCategories = (category: T) => findSubCategories(String(category.id)).length > 0;

  const renderedCategoryTree = (categoryItems: T[]) => {
    if (!categoryItems || !categoryItems.length) {
      return null;
    }

    const branchItems = categoryItems.filter((category) => hasSubCategories(category));
    let accordionRendered = false;

    const renderBranchPanel = (category: T) => {
      const categoryId = String(category.id);
      const subCategories = findSubCategories(categoryId);

      return (
        <Panel
          header={
            <PanelHeader>
              <CategoryNameLabel
                title={category.attributes?.title}
                translatedName={category.attributes?.translatedName}
              />
            </PanelHeader>
          }
          key={categoryId}
        >
          {renderedCategoryTree(subCategories)}
        </Panel>
      );
    };

    return (
      <CategoryTreeLevel>
        {categoryItems.map((category: T) => {
          const categoryId = String(category.id);

          if (!hasSubCategories(category)) {
            return <React.Fragment key={categoryId}>{renderItem(category)}</React.Fragment>;
          }

          if (accordionRendered) {
            return null;
          }

          accordionRendered = true;
          const accordionKey = branchItems.map((item) => String(item.id)).join('-');

          return (
            <CategoryBranchCollapse key={`accordion-${accordionKey}`}>
              <Collapse accordion>{branchItems.map((branch) => renderBranchPanel(branch))}</Collapse>
            </CategoryBranchCollapse>
          );
        })}
      </CategoryTreeLevel>
    );
  };

  if (!categories || !categories.length) {
    return null;
  }
  return renderedCategoryTree(parentCategories);
}

export default categoryCollapseTree;
